import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import { DrawerBody, DrawerFooter } from "@/components/ui/drawer"
import { toast } from "@/hooks/useToast"
import { isPrescriptionRequestType } from "@/modules/patients/detail/episode-of-care/episode-of-care-dropdown"
import { ChPrescriptionDrawer } from "@/modules/patients/detail/prescription/ch/ch-prescription-drawer"
import { fetchExistingChPrescription } from "@/modules/patients/detail/prescription/ch/services/fetchExistingChPrescription"
import { useChPrescriptionStoreContext } from "@/modules/patients/detail/prescription/ch/store/usePrescriptionStore"
import { LegacyQmsQuestionnaire } from "@/modules/patients/detail/progress-note/components/questionnaires/legacy-qms-questionnaire"
import {
  fetchSurveyLabQuestionnaire,
  SurveyLabQuestionnaire,
} from "@/modules/patients/detail/progress-note/components/questionnaires/survey-lab-questionnaire"
import { useProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { UpdateAppointmentAsyncRequestStatusInput } from "@/modules/patients/services/__generated__/useUpdateAppointmentAsyncRequestStatusMutation.graphql"
import { useUpdateAppointmentQmsAsyncRequestStatusMutation } from "@/modules/patients/services/useUpdateAppointmentAsyncRequestStatusMutation"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import {
  AsyncRequestStatus,
  CommunicationChannel,
  QuestionnaireType,
  RequestType,
} from "@/types/__generated__/union-types"

interface QuestionnaireContentProps {
  isReadOnly: boolean
  appointmentId: string
  questionnaireId: number
  isCompletedAppointment?: boolean
  channel: CommunicationChannel
  requestType?: RequestType
  type: QuestionnaireType
  onClose: () => void
  progressNoteId?: string
  diagnosisCode?: string
}

const QuestionnaireContent = (props: QuestionnaireContentProps) => {
  const {
    onClose,
    isReadOnly,
    appointmentId,
    questionnaireId,
    isCompletedAppointment,
    channel,
    requestType,
    type,
    progressNoteId,
    diagnosisCode,
  } = props

  const { t } = useTranslation()
  const [updateAsyncStatus, isUpdating] = useUpdateAppointmentQmsAsyncRequestStatusMutation()
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const resetChPrescriptionStore = useChPrescriptionStoreContext((state) => state.reset)
  const setSuggestedMedications = useChPrescriptionStoreContext((state) => state.setSuggestedMedications)
  const country = useCountryParam()

  const canAutofillPrescription =
    type === "SURVEY_LAB" &&
    channel === "ASYNC_REQUEST" &&
    country === "CH" &&
    requestType !== undefined &&
    isPrescriptionRequestType(requestType) &&
    progressNoteId !== undefined &&
    diagnosisCode !== undefined

  const progressNoteFormStore = useProgressNoteFormContext((state) => state.progressNoteForms)

  const questionnaireForms = useMemo(() => {
    return Array.from(progressNoteFormStore.values()).map((item) => item.questionnaireForm)
  }, [progressNoteFormStore])

  const onUpdateAsyncRequestStatus = (status: AsyncRequestStatus) => {
    confirm({
      title: status === "APPROVED" ? t("common.button.approve") : t("common.button.reject"),
      content: t("common.confirmation.saveDisabledNewChatTitle"),
      cancelText: t("common.button.cancel"),
      confirmText: t("common.button.save"),
      onConfirm: () => {
        const payloadUpdate: UpdateAppointmentAsyncRequestStatusInput = {
          id: appointmentId,
          asyncRequestStatus: status,
        }
        updateAsyncStatus({
          variables: {
            input: payloadUpdate,
          },
          onCompleted: (response) => {
            const errMessage = response.updateAppointmentAsyncRequestStatus?.errors?.[0]?.message
            cancel()
            if (questionnaireForms) {
              for (const form of questionnaireForms) {
                form?.setValue("questionnaireStatus", true)
                form?.trigger("questionnaireStatus")
              }
            }

            if (errMessage) {
              toast({ description: errMessage })
              return
            }

            if (status === "APPROVED" && canAutofillPrescription) {
              openPrescriptionDrawerWithSuggestions()
              return
            }

            onClose()
          },
        })
      },
      onCancel: cancel,
    })
  }

  const openPrescriptionDrawer = async (suggestedMedications: string[]) => {
    if (progressNoteId === undefined || diagnosisCode === undefined) return

    const prescriptionResult = await fetchExistingChPrescription(progressNoteId).catch(() => null)
    const existingPrescription = prescriptionResult?.ch_progressNoteById?.ch_prescription ?? undefined

    onClose()
    openDrawer(
      () => (
        <ChPrescriptionDrawer
          progressNoteId={progressNoteId}
          diagnosisCode={diagnosisCode}
          id={existingPrescription?.id}
          prescriptionRef={existingPrescription}
        />
      ),
      "PrescriptionDrawer",
      {
        onBeforeOpen: () => {
          resetChPrescriptionStore()
          setSuggestedMedications(suggestedMedications)
        },
      },
    )
  }

  const openPrescriptionDrawerWithSuggestions = async () => {
    const result = await fetchSurveyLabQuestionnaire(appointmentId).catch(() => null)
    await openPrescriptionDrawer([
      ...(result?.surveyLabQuestionnaireSummaryByAppointmentId?.suggestedMedications ?? []),
    ])
  }

  return (
    <>
      <DrawerBody className="h-full p-0">
        <ScrollWrapper className="scrollbar-thumb-rounded-full scrollbar-w-1 hover:scrollbar-thumb-surface-pressed scrollbar-thin scrollbar-thumb-transparent scrollbar-track-transparent p-4">
          {type === "QMS" && <LegacyQmsQuestionnaire questionnaireId={questionnaireId} />}
          {type === "SURVEY_LAB" && <SurveyLabQuestionnaire appointmentId={appointmentId} />}
        </ScrollWrapper>
      </DrawerBody>
      <DrawerFooter className="justify-end gap-2 overflow-hidden">
        <Button variant="secondary" onClick={onClose} data-testid="close-button">
          {t("common.button.close")}
        </Button>
        {!isReadOnly && !isCompletedAppointment && channel === "ASYNC_REQUEST" && (
          <>
            <Button variant="danger" onClick={() => onUpdateAsyncRequestStatus("REJECTED")}>
              {t("common.button.reject")}
            </Button>
            <Button isLoading={isUpdating} variant="primary" onClick={() => onUpdateAsyncRequestStatus("APPROVED")}>
              {t("common.button.approve")}
            </Button>
          </>
        )}
      </DrawerFooter>
    </>
  )
}

export { QuestionnaireContent }
